<template>
  <div class="flex flex-col w-full">

    <!-- Nombre de la capa de diseño: una sola línea, ancho completo. -->
    <div class="mb-3">
      <label class="label">{{ $t('design_layer_name') }}</label>
      <div class="flex items-center py-1">
        <layer-color-icon :color="layer && layer.color" class="mr-2" />
        <span class="font-semibold">{{ layer && layer.name }}</span>
      </div>
    </div>

    <!-- Línea siguiente: Tipo BOQ (bloqueado) + ID orden de pedido (eléctrico). -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <b-field :label="$t('boq_type')">
        <b-input :value="$t(boqTypeLabel)" readonly disabled />
      </b-field>

      <!-- Orden de pedido (solo eléctrico/zanjas). -->
      <b-field v-if="!isMechanical" :label="$t('order_id')">
        <multiselect v-model="selectedOrder"
                     :options="workOrders"
                     label="code"
                     track-by="id"
                     :show-labels="false"
                     :searchable="true"
                     :allow-empty="true"
                     :loading="isLoading"
                     :placeholder="$t('select_work_order')">
          <template slot="noResult">{{ $t('no_elements_found') }}</template>
          <template slot="noOptions">{{ $t('no_options_available') }}</template>
        </multiselect>
      </b-field>

      <!-- BOQ / carga (mecánico): al elegirla se cargan las líneas de pedido de
           estructuras y módulos (pictogram_boqs de esa carga). -->
      <b-field v-else :label="$t('boq')">
        <multiselect v-model="selectedBoqHeader"
                     :options="boqHeaders"
                     label="name"
                     track-by="id"
                     :show-labels="false"
                     :searchable="true"
                     :allow-empty="true"
                     :loading="isLoading"
                     :placeholder="$t('select_boq')">
          <template slot="noResult">{{ $t('no_elements_found') }}</template>
          <template slot="noOptions">{{ $t('no_options_available') }}</template>
        </multiselect>
      </b-field>
    </div>

    <!-- MECÁNICO: estructura + módulos (dos pares línea/orden por capa). -->
    <template v-if="isMechanical">
      <!-- ESTRUCTURA -->
      <div class="border-t pt-3 mb-4">
        <h3 class="font-semibold uppercase text-sm text-gray-600 mb-2">{{ $t('structures') }}</h3>
        <div class="grid grid-cols-2 gap-4">
          <b-field :label="$t('structure_order_line_id')">
            <b-select v-model="form.structureLineId"
                      :placeholder="form.boqId ? $t('select_boq') : $t('select_boq_first')"
                      :loading="isLoading"
                      :disabled="!form.boqId"
                      expanded>
              <option :value="null">—</option>
              <option v-for="boq in linesForHeader" :key="`sl-${boq.id}`" :value="boq.id">
                {{ boq.code }} ({{ boq.line_pedido || '—' }})
              </option>
            </b-select>
          </b-field>
          <b-field :label="$t('structure_order_id')">
            <b-select v-model="form.structureOrderId"
                      :placeholder="$t('select_work_order')"
                      :loading="isLoading"
                      expanded>
              <option :value="null">—</option>
              <option v-for="wo in workOrders" :key="`so-${wo.id}`" :value="wo.id">
                {{ wo.code }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>

      <!-- MÓDULOS -->
      <div class="border-t pt-3 mb-4">
        <h3 class="font-semibold uppercase text-sm text-gray-600 mb-2">{{ $t('modules') }}</h3>
        <div class="grid grid-cols-2 gap-4">
          <b-field :label="$t('module_order_line_id')">
            <b-select v-model="form.moduleLineId"
                      :placeholder="form.boqId ? $t('select_boq') : $t('select_boq_first')"
                      :loading="isLoading"
                      :disabled="!form.boqId"
                      expanded>
              <option :value="null">—</option>
              <option v-for="boq in linesForHeader" :key="`ml-${boq.id}`" :value="boq.id">
                {{ boq.code }} ({{ boq.line_pedido || '—' }})
              </option>
            </b-select>
          </b-field>
          <b-field :label="$t('module_order_id')">
            <b-select v-model="form.moduleOrderId"
                      :placeholder="$t('select_work_order')"
                      :loading="isLoading"
                      expanded>
              <option :value="null">—</option>
              <option v-for="wo in workOrders" :key="`mo-${wo.id}`" :value="wo.id">
                {{ wo.code }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </template>

    <!-- ELÉCTRICO / ZANJAS: cascada carga → línea.
         1) BOQ / carga: cabeceras de la tabla boqs (por tipo).
         2) Línea de pedido: filas de pictogram_boqs de la carga elegida. -->
    <template v-else>
      <div class="border-t pt-3 mb-4">
        <div class="grid grid-cols-2 gap-4">
          <b-field :label="$t('boq')">
            <multiselect v-model="selectedBoqHeader"
                         :options="boqHeaders"
                         label="name"
                         track-by="id"
                         :show-labels="false"
                         :searchable="true"
                         :allow-empty="true"
                         :loading="isLoading"
                         :placeholder="$t('select_boq')">
              <template slot="noResult">{{ $t('no_elements_found') }}</template>
              <template slot="noOptions">{{ $t('no_options_available') }}</template>
            </multiselect>
          </b-field>
          <b-field :label="$t('order_line_id')">
            <multiselect v-model="selectedLine"
                         :options="linesForHeader"
                         :custom-label="lineLabel"
                         track-by="id"
                         :show-labels="false"
                         :searchable="true"
                         :allow-empty="true"
                         :loading="isLoading"
                         :disabled="!form.boqId"
                         :placeholder="form.boqId ? $t('select') : $t('select_boq_first')">
              <template slot="noResult">{{ $t('no_elements_found') }}</template>
              <template slot="noOptions">{{ $t('no_options_available') }}</template>
            </multiselect>
          </b-field>
        </div>
      </div>
    </template>

    <!-- Botones: Cancelar en el extremo izquierdo, Guardar en el derecho. -->
    <div class="flex justify-between border-t pt-4">
      <b-button @click="_HandleCancel">{{ $t('cancel') }}</b-button>
      <b-button type="is-primary" :loading="isSaving" @click="_HandleSave">
        {{ $t('save') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import LayerColorIcon from '@pictograms/shared/components/icons/LayerColorIcon.vue';

export default {
  name: 'PictogramBoqLayerLinksModal',

  components: {
    LayerColorIcon,
    Multiselect,
  },

  props: {
    projectId:     { type: Number, required: true },
    pictogramId:   { type: Number, required: true },
    // 'mechanical_installation' | 'electrical_installation' | ... (constantes Pictogram)
    pictogramType: { type: String, required: true },
    layer:         { type: Object, required: true },
  },

  data() {
    return {
      isLoading: false,
      isSaving:  false,
      boqs:       [],
      workOrders: [],
      form: {
        // Mecánico: dos pares línea/orden (estructura + módulos).
        structureLineId:  null,
        structureOrderId: null,
        moduleLineId:     null,
        moduleOrderId:    null,
        // Eléctrico / zanjas: cabecera boqs (carga/zona) → línea pictogram_boqs + orden.
        boqId:   null,
        lineId:  null,
        orderId: null,
      },
    };
  },

  computed: {
    // El pictograma es mecánico cuando su type es 'mechanical_installation'.
    // Eléctrico e zanjas comparten la variante de "una línea/orden por capa".
    isMechanical() {
      return this.pictogramType === 'mechanical_installation';
    },
    // Mapea el type del Pictogram → type de la BOQ. Eléctrico y zanjas
    // comparten el catálogo BOQ 'electrical' (no existe TYPE_TRENCHES).
    boqType() {
      return this.isMechanical ? 'mechanical' : 'electrical';
    },
    // Clave i18n del tipo mostrado (readonly). Los pictogram types tienen
    // traducción propia (mechanical_installation / electrical_installation /
    // electrical_trenches).
    boqTypeLabel() {
      return this.pictogramType;
    },

    // Proxies objeto↔id para los multiselect (eléctrico). El form guarda el id
    // (fuente de verdad, precargado en _PreloadForm antes de que lleguen las
    // opciones); el get resuelve el objeto una vez cargadas boqs/workOrders.

    // 1) Cabeceras (tabla boqs) del tipo — derivadas de las filas cargadas
    // (cada pictogram_boq trae su cabecera embebida en `boq`).
    boqHeaders() {
      const map = new Map();
      for (const row of this.boqs) {
        if (row.boq && !map.has(row.boq.id)) map.set(row.boq.id, row.boq);
      }
      return Array.from(map.values());
    },
    selectedBoqHeader: {
      get() {
        return this.boqHeaders.find(h => h.id === this.form.boqId) || null;
      },
      set(v) {
        this.form.boqId = v ? v.id : null;
        // Al cambiar de carga se limpian las líneas previas (pertenecían a otra
        // carga): eléctrico (lineId) y mecánico (estructura/módulo).
        this.form.lineId = null;
        this.form.structureLineId = null;
        this.form.moduleLineId = null;
      },
    },

    // 2) Líneas (pictogram_boqs) de la carga elegida. Vacío hasta elegir carga.
    linesForHeader() {
      if (!this.form.boqId) return [];
      return this.boqs.filter(b => b.boq_id === this.form.boqId);
    },
    // Línea (pictogram_boqs) elegida: fuente de verdad del vínculo eléctrico.
    selectedLine: {
      get() {
        return this.boqs.find(b => b.id === this.form.lineId) || null;
      },
      set(v) {
        this.form.lineId = v ? v.id : null;
      },
    },
    selectedOrder: {
      get() {
        return this.workOrders.find(w => w.id === this.form.orderId) || null;
      },
      set(v) {
        this.form.orderId = v ? v.id : null;
      },
    },
  },

  mounted() {
    this._PreloadForm();
    this._LoadOptions();
  },

  methods: {
    // Prellenar el form con los IDs guardados en layer.boqLinks (snapshot Mongo).
    _PreloadForm() {
      const l = this.layer && this.layer.boqLinks;
      if (!l) return;
      if (this.isMechanical) {
        this.form.structureLineId  = (l.structureLine  && l.structureLine.id_mysql)  || null;
        this.form.structureOrderId = (l.structureOrder && l.structureOrder.id_mysql) || null;
        this.form.moduleLineId     = (l.moduleLine     && l.moduleLine.id_mysql)     || null;
        this.form.moduleOrderId    = (l.moduleOrder    && l.moduleOrder.id_mysql)    || null;
      } else {
        // La BOQ guardada (id_mysql = fila plana) ya identifica línea + carga/zona.
        this.form.lineId  = (l.line  && l.line.id_mysql)  || null;
        this.form.orderId = (l.order && l.order.id_mysql) || null;
      }
    },

    async _LoadOptions() {
      this.isLoading = true;
      try {
        // BOQ del tipo (filas planas, cada una con su cabecera boq = carga/zona)
        // y órdenes de pedido.
        const [boqRes, woRes] = await Promise.all([
          axios.get(`/api/v2/project/${this.projectId}/boq-details`, { params: { type: this.boqType } }),
          axios.get(`/api/v2/work-orders`, { params: { project_id: this.projectId } }),
        ]);
        this.boqs       = (boqRes.data && boqRes.data.boqs) || [];
        this.workOrders = (woRes.data  && woRes.data.data)  || [];

        // Derivar la cabecera (carga) de la(s) línea(s) preseleccionada(s) para
        // que el selector de carga la muestre y los de línea filtren sus opciones.
        if (!this.form.boqId) {
          const seedLineId = this.isMechanical
            ? (this.form.structureLineId || this.form.moduleLineId)
            : this.form.lineId;
          const row = seedLineId ? this.boqs.find(b => b.id === seedLineId) : null;
          if (row) this.form.boqId = row.boq_id;
        }
      } catch (e) {
        this.$notify.error(this.$t('error_loading_documents'));
      } finally {
        this.isLoading = false;
      }
    },

    // Construye snapshot denormalizado de la BOQ.
    // Nota: Eloquent decimal devuelve string ("78.00") → coerción a Number para
    // que Mongo persista como Double, no como String.
    //   - mecánico: cantidad/precio desde las columnas de pictogram_boqs.
    //   - eléctrico: cantidad/precio inline de la fila + carga/zona = boq.name
    //     (zone_code/zone_name alimentan zone_bqo en la propagación por capa).
    _BuildBoqSnapshot(id) {
      if (!id) return null;
      const boq = this.boqs.find(b => b.id === id);
      if (!boq) return null;

      const snap = {
        id_mysql:    boq.id,
        code:        boq.code,
        line_pedido: boq.line_pedido,
        description: boq.description,
        unit:        boq.unit,
      };

      if (this.isMechanical) {
        snap.priceUnit = this._ToNumber(boq.price_unit);
      } else {
        // La BOQ (fila plana) ya es (línea + carga/zona): cantidad/precio inline;
        // la carga/zona = nombre de la cabecera (boq.name) → alimenta zone_bqo.
        const zoneName  = boq.boq ? boq.boq.name : null;
        snap.zone_code  = zoneName;
        snap.zone_name  = zoneName;
        snap.quantity   = this._ToNumber(boq.quantity);
        snap.priceUnit  = this._ToNumber(boq.price_unit);
        snap.priceTotal = this._ToNumber(boq.price_total);
      }

      return snap;
    },

    _ToNumber(v) {
      if (v === null || v === undefined || v === '') return null;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    },

    // Etiqueta del multiselect de línea (2º selector): "code (line_pedido)".
    // La carga/zona ya la da el 1er selector (cabecera boqs).
    lineLabel(boq) {
      if (!boq) return '';
      return `${boq.code} (${boq.line_pedido || '—'})`;
    },

    _BuildWorkOrderSnapshot(id) {
      if (!id) return null;
      const wo = this.workOrders.find(w => w.id === id);
      if (!wo) return null;
      return {
        id_mysql:    wo.id,
        code:        wo.code,
        description: wo.description,
      };
    },

    async _HandleSave() {
      this.isSaving = true;
      try {
        const payload = this.isMechanical
          ? {
              structureLine:  this._BuildBoqSnapshot(this.form.structureLineId),
              structureOrder: this._BuildWorkOrderSnapshot(this.form.structureOrderId),
              moduleLine:     this._BuildBoqSnapshot(this.form.moduleLineId),
              moduleOrder:    this._BuildWorkOrderSnapshot(this.form.moduleOrderId),
            }
          : {
              line:  this._BuildBoqSnapshot(this.form.lineId),
              order: this._BuildWorkOrderSnapshot(this.form.orderId),
            };
        // El identificador de la capa es `name` (las capas Mongo no tienen id).
        const layerName = encodeURIComponent(this.layer.name);
        const { data } = await axios.patch(
          `/api/v2/pictogram/${this.pictogramId}/design/layer/${layerName}/boq-links`,
          payload
        );
        if (data && data.success) {
          this.$notify.success(this.$t('save'));
          this.$emit('saved', payload);
          this.$emit('close');
        } else {
          this.$notify.error(this.$t('error_saving'));
        }
      } catch (e) {
        this.$notify.error(this.$t('error_saving'));
      } finally {
        this.isSaving = false;
      }
    },

    _HandleCancel() {
      this.$emit('close');
    },
  },
};
</script>
